import { memo, useMemo, useState } from 'react';
import QRious from 'qrious';

import { OButton } from '@src/shared';

import styles from './PanelistInviteQRContent.module.scss';

// Constants
const QR_CODE_SIZE = 235;

interface IPanelistInviteQRContentProps {
  meetingLink: string;
  handleDownloadQR: () => void;
}

const PanelistInviteQRContent = ({ meetingLink, handleDownloadQR }: IPanelistInviteQRContentProps) => {
  const [qrError, setQrError] = useState<string | null>(null);
  
  const qrDataUrl = useMemo(() => {
    if (!meetingLink) {
      setQrError(null);
      return '';
    }

    setQrError(null);
    // Generate QR code with size 235
    // Create a temporary canvas element
    const canvas = document.createElement('canvas');
    // Temporarily add to DOM (QRious may need it)
    canvas.style.display = 'none';
    document.body.appendChild(canvas);
    
    try {
      const qr = new QRious({
        element: canvas,
        value: meetingLink,
        size: QR_CODE_SIZE,
      });
      // Get data URL from canvas
      const dataUrl = canvas.toDataURL();
      return dataUrl;
    } catch (error) {
      console.error('Error generating QR code:', error);
      setQrError('Failed to generate QR code. Please try again.');
      return '';
    } finally {
      // Clean up - check if canvas is still in DOM before removing
      if (canvas.parentNode === document.body) {
        document.body.removeChild(canvas);
      }
    }
  }, [meetingLink]);

  return (
    <div className={styles.qrRoot}>
      <p className={styles.qrDescription}>
        Share the QR code and let people scan it to join
      </p>
      <div className={styles.qrCodeContainer}>
        {qrError ? (
          <div className={styles.qrError} role="alert" aria-live="polite">
            {qrError}
          </div>
        ) : qrDataUrl ? (
          <img
            src={qrDataUrl}
            alt={`QR code for meeting link: ${meetingLink}`}
            className={styles.qrCodeImage}
            aria-label="QR code for meeting invitation"
          />
        ) : (
          <div className={styles.qrLoading} aria-label="Generating QR code">
            Generating QR code...
          </div>
        )}
      </div>
      <OButton
        text="Download QR"
        variant="success"
        size="default"
        onClick={handleDownloadQR}
        classes={{ container: styles.downloadButton }}
        aria-label="Download QR code as PNG image"
        disabled={!qrDataUrl || !!qrError}
      />
    </div>
  );
};

export default memo<IPanelistInviteQRContentProps>(PanelistInviteQRContent);

